import { strainQuerySchema, type Strain, type StrainQuery } from '@shared';

function includesSearch(strain: Strain, search: string): boolean {
  const needle = search.toLowerCase();
  const fields = [
    strain.name,
    strain.productName,
    strain.producer,
    strain.brand,
    strain.productId,
    strain.lineageSourceText,
    strain.lotNumber,
    strain.notes,
  ];
  return fields.some(
    (value) => value != null && value.toLowerCase().includes(needle),
  );
}

function isSpreadsheetManaged(strain: Strain): boolean {
  return (
    strain.sourceType === 'spreadsheet' ||
    (strain.productId != null && strain.productId !== '')
  );
}

function displayName(strain: Strain): string {
  return (strain.name ?? strain.productName ?? '').toLowerCase();
}

/**
 * Apply catalogue list filters/sort locally, matching server list semantics
 * (including hiding incomplete package-draft saves).
 */
export function filterAndSortStrains(
  strains: Strain[],
  partialQuery: Partial<StrainQuery> = {},
): Strain[] {
  const query = strainQuerySchema.parse(partialQuery);
  const filtered = strains.filter((strain) => {
    if (strain.saveStatus === 'incomplete') return false;

    if (query.archived === 'active' && strain.isArchived) return false;
    if (query.archived === 'archived' && !strain.isArchived) return false;

    if (query.verified === 'verified' && !strain.isVerified) return false;
    if (query.verified === 'unverified' && strain.isVerified) return false;

    if (query.category && strain.category !== query.category) return false;
    if (query.producer && strain.producer !== query.producer) return false;

    if (query.source === 'spreadsheet' && !isSpreadsheetManaged(strain)) {
      return false;
    }
    if (query.source === 'local' && isSpreadsheetManaged(strain)) {
      return false;
    }

    if (query.search && !includesSearch(strain, query.search)) return false;
    return true;
  });

  const sorted = [...filtered];
  switch (query.sort) {
    case 'name':
      sorted.sort((a, b) => displayName(a).localeCompare(displayName(b)));
      break;
    case 'thc_desc':
      sorted.sort((a, b) => b.thcPercent - a.thcPercent);
      break;
    case 'thc_asc':
      sorted.sort((a, b) => a.thcPercent - b.thcPercent);
      break;
    case 'recent':
    default:
      sorted.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      break;
  }
  return sorted;
}

export function computeCatalogueMeta(strains: Strain[]): {
  total: number;
  producers: string[];
  categories: string[];
} {
  const complete = strains.filter(
    (s) => s.saveStatus === 'complete' || s.saveStatus == null,
  );
  const active = complete.filter((s) => !s.isArchived);
  const producers = [
    ...new Set(
      complete
        .map((s) => s.producer)
        .filter((p): p is string => Boolean(p && p.trim())),
    ),
  ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const categories = [
    ...new Set(
      complete
        .map((s) => s.category)
        .filter((c): c is string => Boolean(c && c.trim())),
    ),
  ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return { total: active.length, producers, categories };
}
